import type { RosterGroup, RosterRow } from './roster.js';

/**
 * The filter engines — two of them (design 186–208, 1019–1067).
 *
 * Search plus a tri-state facet grid, and below it a written-predicate engine.
 *
 * **They are two engines on purpose, and this module's own comment used to claim
 * otherwise.** Phase 2 wrote that Phase 4's recognition table would lift "this
 * whole module — same engine, different rows", and that `matches` took a row
 * shape rather than a `RosterRow`. Neither held: the signature says `RosterRow`,
 * and the deeper mismatch is the question being asked. A facet answers *"which of
 * the values present in these rows do I want"* and derives its vocabulary from
 * the rows; the recognition table asks the opposite, where the operator supplies
 * a number the table has never seen and the row answers with a **band**. A
 * tri-state button cannot express that. So the module gained a second engine
 * rather than a generalisation of the first, and `rails/FacetRow.tsx` was not
 * lifted at all.
 *
 * ## Deriving the values rather than declaring them
 *
 * A facet lists only the values that actually occur in the rows the viewer was
 * given. Declaring `STATUS: NOMINAL · DEGRADED · OFFLINE · DESTROYED` up front
 * would draw two buttons that match nothing and quietly assert that the other
 * two states exist somewhere — and the first thing an operator does with a
 * filter is read it as an inventory.
 *
 * ## Counting
 *
 * Must-not #1: **no count of anything the viewer was not given**, and the copy
 * rules add: never "showing 5 of 12". So there is exactly one number in the
 * whole filter surface — the count of *armed facet values*, next to the word
 * FILTER, which is a count of the operator's own clicks. Group headers carry the
 * number of rows they are showing, which the operator can also count by eye.
 * Neither is ever a fraction.
 */

export type FacetState = 'OFF' | 'INCLUDE' | 'EXCLUDE';

/** The tri-state cycle a facet button walks: off, keep only these, drop these. */
export function nextFacetState(state: FacetState): FacetState {
  return state === 'OFF' ? 'INCLUDE' : state === 'INCLUDE' ? 'EXCLUDE' : 'OFF';
}

export type FacetId = 'ZONE' | 'SEGMENT' | 'STATUS' | 'RUNG';

export const FACET_LABELS: Readonly<Record<FacetId, string>> = Object.freeze({
  // The facet's key stays ZONE — it is an internal id shared with the context
  // menu's cycleFacet call. The word an operator reads is the rulebook's.
  ZONE: 'REGION',
  SEGMENT: 'SEGMENT',
  STATUS: 'STATUS',
  RUNG: 'TRACK',
});

export const FACET_ORDER: readonly FacetId[] = Object.freeze([
  'ZONE',
  'SEGMENT',
  'STATUS',
  'RUNG',
] as const);

/** `facetId -> value -> state`. Absent is `OFF`; nothing is stored for `OFF`. */
export type FacetSelection = Readonly<Record<string, Readonly<Record<string, FacetState>>>>;

export const NO_FACETS: FacetSelection = Object.freeze({});

export interface FacetValue {
  readonly value: string;
  readonly state: FacetState;
}

export interface Facet {
  readonly id: FacetId;
  readonly label: string;
  readonly values: readonly FacetValue[];
}

/** What a row offers to each facet. `null` means the facet does not apply to it. */
function valueOf(row: RosterRow, facet: FacetId): string | null {
  switch (facet) {
    case 'ZONE':
      return row.zone;
    case 'SEGMENT':
      return row.segment;
    case 'STATUS':
      return row.status;
    case 'RUNG':
      return row.rung;
  }
}

export function facetStateOf(selection: FacetSelection, facet: FacetId, value: string): FacetState {
  return selection[facet]?.[value] ?? 'OFF';
}

/** The facet grid for a set of rows, with the operator's current selection applied. */
export function facetsOf(rows: readonly RosterRow[], selection: FacetSelection): readonly Facet[] {
  return FACET_ORDER.map((id) => {
    const values = [...new Set(rows.map((row) => valueOf(row, id)).filter(isPresent))].sort();
    return {
      id,
      label: FACET_LABELS[id],
      values: values.map((value) => ({ value, state: facetStateOf(selection, id, value) })),
    };
  }).filter((facet) => facet.values.length > 0);
}

function isPresent(value: string | null): value is string {
  return value !== null;
}

/** How many facet values are armed. The one number the filter block shows. */
export function armedCount(selection: FacetSelection): number {
  return Object.values(selection).reduce(
    (total, values) => total + Object.values(values).filter((state) => state !== 'OFF').length,
    0,
  );
}

/**
 * Does a row survive?
 *
 * Within a facet: any `EXCLUDE` on the row's value drops it; if the facet has
 * any `INCLUDE` at all, the row must match one of them. Across facets: AND.
 *
 * A row a facet does not apply to — a network has no rung — survives that
 * facet's `INCLUDE` set. The alternative reads as "TRACK: FIXED" hiding every
 * friendly asset on the console, which is not what an operator means by it.
 */
export function matchesFacets(row: RosterRow, selection: FacetSelection): boolean {
  return FACET_ORDER.every((facet) => {
    const chosen = selection[facet];
    if (chosen === undefined) return true;
    const value = valueOf(row, facet);
    if (value !== null && chosen[value] === 'EXCLUDE') return false;
    const includes = Object.entries(chosen).filter(([, state]) => state === 'INCLUDE');
    if (includes.length === 0) return true;
    if (value === null) return true;
    return includes.some(([included]) => included === value);
  });
}

export function matchesSearch(row: RosterRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  return needle === '' || row.haystack.includes(needle);
}

export function matches(row: RosterRow, search: string, selection: FacetSelection): boolean {
  return matchesSearch(row, search) && matchesFacets(row, selection);
}

/** The roster, filtered. Groups left with no rows drop out entirely. */
export function filterRoster(
  groups: readonly RosterGroup[],
  search: string,
  selection: FacetSelection,
): readonly RosterGroup[] {
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) => matches(row, search, selection)),
    }))
    .filter((group) => group.rows.length > 0);
}

/** Every row in every group, in render order — what arrow-stepping walks. */
export function flatten(groups: readonly RosterGroup[]): readonly RosterRow[] {
  return groups.flatMap((group) => group.rows);
}

// ---------------------------------------------------------------------------
// The predicate engine — Phase 4's lift, and not the facet engine above
// ---------------------------------------------------------------------------

/**
 * Written predicates: `COLUMN OP VALUE`, ANDed.
 *
 * **This is a second engine in the same module, not a generalisation of the
 * first**, and the honest reason is that the ledger's expected lift did not hold.
 * The facet engine above answers "which of the values *present in these rows* do
 * I want", and it derives its vocabulary from the rows. The recognition table
 * asks the opposite question — "which types could produce a boost of 180 s" —
 * where the operator supplies a number the table has never seen and the row
 * answers with a **band**. A tri-state button cannot express that, so the lift is
 * the module and the tests, not the code path.
 *
 * ## Bands, not values, and `null` is not a zero
 *
 * A catalogue row carries `[min, max]`, so a comparison asks whether any part of
 * the row's range satisfies it. A row whose band is `null` — `apogeeKm` on a
 * space launch, a direct-ascent ASAT and a test shot — **fails every numeric
 * comparison on that column**. It is not zero and it is not infinity: there is no
 * ballistic apogee to compare, so no apogee criterion can be met. Treating it as
 * a zero would quietly exclude every space launch from `APOGEE > 200` *and*
 * silently include it in `APOGEE < 200`, which is worse than either.
 */

export type FilterKind = 'num' | 'cat' | 'text';

export const NUM_OPS = ['<', '≤', '=', '≥', '>', 'BETWEEN'] as const;
export const CAT_OPS = ['IS', 'IS NOT'] as const;
export const TEXT_OPS = ['CONTAINS'] as const;

export type NumOp = (typeof NUM_OPS)[number];
export type CatOp = (typeof CAT_OPS)[number];
export type TextOp = (typeof TEXT_OPS)[number];
export type FilterOp = NumOp | CatOp | TextOp;

export const OPS_FOR: Readonly<Record<FilterKind, readonly FilterOp[]>> = Object.freeze({
  num: NUM_OPS,
  cat: CAT_OPS,
  text: TEXT_OPS,
});

export function defaultOpFor(kind: FilterKind): FilterOp {
  return kind === 'num' ? '=' : kind === 'cat' ? 'IS' : 'CONTAINS';
}

/** One written row of the filter block. `b` is read by `BETWEEN` and by nothing else. */
export interface FilterPredicate {
  readonly id: string;
  readonly column: string;
  readonly op: FilterOp;
  readonly a: string;
  readonly b: string;
  /** The tick box. An unticked row keeps its values and stops matching. */
  readonly on: boolean;
}

/** What a row offers one column: a band, or a word. */
export type FilterValue =
  | { readonly kind: 'num'; readonly band: readonly [number, number] | null }
  | { readonly kind: 'cat'; readonly text: string }
  | { readonly kind: 'text'; readonly text: string };

/**
 * Is this predicate doing anything?
 *
 * A row with no value typed into it is **inert**, not empty-matching: the
 * operator is mid-sentence, and a table that emptied itself between the column
 * and the number would be unusable.
 */
export function predicateArmed(predicate: FilterPredicate): boolean {
  if (!predicate.on || predicate.column === '') return false;
  if (predicate.a.trim() === '') return false;
  return predicate.op !== 'BETWEEN' || predicate.b.trim() !== '';
}

export function armedPredicates(
  predicates: readonly FilterPredicate[],
): readonly FilterPredicate[] {
  return predicates.filter(predicateArmed);
}

function finite(text: string): number | null {
  const value = Number(text.trim());
  return text.trim() !== '' && Number.isFinite(value) ? value : null;
}

/** Band overlap. `null` band ⇒ no comparison on this column can be satisfied. */
export function matchesBand(
  band: readonly [number, number] | null,
  op: FilterOp,
  a: string,
  b: string,
): boolean {
  if (band === null) return false;
  const value = finite(a);
  // Not a number yet: inert, for `predicateArmed`'s reason.
  if (value === null) return true;
  const [lo, hi] = band;
  switch (op) {
    case '<':
      return lo < value;
    case '≤':
      return lo <= value;
    case '=':
      return lo <= value && value <= hi;
    case '≥':
      return hi >= value;
    case '>':
      return hi > value;
    case 'BETWEEN': {
      const other = finite(b);
      if (other === null) return true;
      return hi >= Math.min(value, other) && lo <= Math.max(value, other);
    }
    default:
      return true;
  }
}

export function matchesCategory(value: string, op: FilterOp, a: string): boolean {
  return op === 'IS NOT' ? value !== a : value === a;
}

export function matchesText(value: string, a: string): boolean {
  return value.toUpperCase().includes(a.trim().toUpperCase());
}

/** One predicate against one row's value. An unknown column is inert. */
export function matchesPredicate(value: FilterValue | null, predicate: FilterPredicate): boolean {
  if (value === null) return true;
  switch (value.kind) {
    case 'num':
      return matchesBand(value.band, predicate.op, predicate.a, predicate.b);
    case 'cat':
      return matchesCategory(value.text, predicate.op, predicate.a);
    case 'text':
      return matchesText(value.text, predicate.a);
  }
}

/** Every armed predicate, ANDed. */
export function applyPredicates<Row>(
  rows: readonly Row[],
  predicates: readonly FilterPredicate[],
  valueOf: (row: Row, column: string) => FilterValue | null,
): readonly Row[] {
  const armed = armedPredicates(predicates);
  if (armed.length === 0) return rows;
  return rows.filter((row) =>
    armed.every((predicate) => matchesPredicate(valueOf(row, predicate.column), predicate)),
  );
}
